import React from 'react';
import { useSelector } from 'react-redux';

import { List, ListItem, Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { includes, omit, startCase } from 'lodash';

import { selectCurrentMasterSettings } from '@lib/selectors';

import { FiltersBadge, Typography } from 'components/v2';
import { isUsingOutreachExperience } from 'utils/outreachExperience';

import { useDialogContext, useFiltersContext } from '../FiltersDialogContext';

const styles = ({
  custom: {
    palette: { colorSupportSteel },
  },
  palette: {
    common: { white },
    primary,
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    sidebar: {
      width: '30%',
      backgroundColor: primary.main,
    },
    sidebarHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: unit * 3,
    },
    sidebarHeaderTitle: {
      color: white,
    },
    sidebarHeaderResetBtn: {
      color: white,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    sidebarListItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: white,
      fontSize: 15,
      fontWeight: 500,
      height: 42,
      padding: `${unit}px ${unit * 3}px`,
      '&:hover': {
        backgroundColor: colorSupportSteel,
      },
    },
    sidebarListItemSelected: {
      backgroundColor: `${colorSupportSteel} !important`,
    },
  });

export interface FiltersSidebarDialogProps extends WithStyles<typeof styles> {
  startCaseLabel?: boolean;
}

const FiltersSidebarDialog: React.FC<FiltersSidebarDialogProps> = ({ classes, startCaseLabel }) => {
  const masterSettings = useSelector(selectCurrentMasterSettings);
  const isToUseOutreachExperience = isUsingOutreachExperience(masterSettings);
  const { tabs, changeTab, selectedTab, toggleLockFilters, resetFiltersShape } = useDialogContext();
  const { filters, reset, count } = useFiltersContext();

  const handleReset = () => {
    reset(omit(resetFiltersShape, 'filtersQueryParams'));
    toggleLockFilters(false);
  };

  const badgeCount = (tab: string) => {
    if (filters[tab].includes('all') && filters[tab].length === 1) return 'All';
    if (filters[tab].includes('all') && filters[tab].length > 1) {
      const filterCount = tab === 'provider' && count.schedulerProvider ? count.schedulerProvider : count[tab];
      return filterCount - filters[tab].length + 1;
    }
    return filters[tab].length;
  };

  const getLabel = (tab: string) => {
    if (tab === 'referralStatus' && isToUseOutreachExperience) return 'Outreach Status';
    switch (tab) {
      case 'messageRef':
        return 'Message Type';
      case 'messageType':
        return 'Direction';
      case 'messageChannel':
        return 'Channel';
      case 'broadcastListDateRange':
        return 'Date Range';
      case 'schedulingPlus':
        return 'Scheduling+';
      case 'formName':
        return 'Form';
      case 'forms':
        return 'Intake Forms';
      case 'ccdaForms':
        return 'CCDA Forms';
      case 'formProcessingStatus':
        return 'Form Status';
      case 'reminderType':
        return 'Type';
      case 'dateRangeFuture':
        return 'Date Range';
      case 'dateRangeWithoutCustom':
        return 'Date Range';
      case 'apptTypeAttributes':
        return 'Appt Type Attributes';
      case 'facilityAttributes':
        return 'Facility Attributes';
      case 'providerAttributes':
        return 'Provider Attributes';
      case 'integrationFlags':
        return 'Integration Flag';
      case 'sentToEhrStatus':
        return 'Sent to EHR Status';
      case 'roles':
        return 'Role';
      case 'recallsStatus':
        return 'Status';
      case 'ccdaOrIntakeFormTemplate':
        return 'Form Template';
      default:
        return startCaseLabel ? startCase(tab) : tab;
    }
  };

  const tabHasLabel = (tab: any) =>
    !includes(
      [
        'dateRange',
        'additionalDateRange',
        'dateRangeFuture',
        'dateRangeWithoutCustom',
        'broadcastListDateRange',
        'dateAggregation',
      ],
      tab,
    );

  return (
    <div className={classes.sidebar}>
      <div className={classes.sidebarHeader}>
        <Typography className={classes.sidebarHeaderTitle} variant="h1SemiBold">
          Filters
        </Typography>
        <div onClick={handleReset} className={classes.sidebarHeaderResetBtn}>
          Reset
        </div>
      </div>
      <List>
        {tabs.map((tab: any) => (
          <ListItem
            key={tab}
            button
            onClick={() => changeTab(tab)}
            classes={{
              root: classes.sidebarListItem,
              selected: classes.sidebarListItemSelected,
            }}
            selected={selectedTab === tab}
          >
            {resetFiltersShape[tab] && resetFiltersShape[tab].customLabel
              ? resetFiltersShape[tab].customLabel
              : getLabel(tab)}
            {filters[tab] && tabHasLabel(tab) && <FiltersBadge badge={badgeCount(tab)} />}
          </ListItem>
        ))}
      </List>
    </div>
  );
};

export default withStyles(styles)(FiltersSidebarDialog);
